/* ---------- React ---------- */
import React from 'react';
import PropTypes from 'prop-types';

/* ---------- Components ---------- */
import EnhancedTable from './EnhancedTable';
import { ComponentDictionary } from '../components';

/* ---------- Utils ---------- */
import defaultGridProps from '../utils/defaultProps';
import helperUtil from '../utils/helper.util';
import settingsStyles from '../styles/settings.styles';

const DISPLAY_NAME = 'SettingsTable';
const { isNumeric, noop } = helperUtil;

const SettingsDecorator = BaseGrid =>
  class SettingsTable extends EnhancedTable {
    static displayName = DISPLAY_NAME;

    static propTypes = {
      dataTest: PropTypes.string,
      onCellChange: PropTypes.func,
      tableId: PropTypes.string,
      minWidth: PropTypes.string,
      caption: PropTypes.string
    };

    static defaultProps = {
      ...defaultGridProps,
      dataTest: '',
      onCellChange: noop,
      tableId: 'settingsTable',
      minWidth: '1600px',
      caption: 'Upload files',
      pageSize: 99, // pageSize must be greater than number of rows shown to avoid pagination
      hidePadRows: true,
      variant: 'settings'
    };

    constructor(props) {
      super({ ...props, childDisplayName: DISPLAY_NAME });

      this.styles = {
        ...settingsStyles,
        tableWrapper: settingsStyles.tableWrapper({ minWidth: props.minWidth })
      };

      this.WIDGETS = ['checkbox'];
    }

    getCellWidthStyle = (headerIdx, cellType) => {
      const { props, styles } = this;
      const width = props.headers?.[headerIdx]?.width || 20;
      return styles.columnWidth(width, cellType);
    };

    getTheadProps = () => ({
      className: isNumeric(this.props.height) ? 'paddingHeader' : ''
    });

    getThProps = headerIdx => ({ css: this.getCellWidthStyle(headerIdx) });

    getTbodyProps = () => ({
      css: theme => this.styles.tBodyStyle(theme, this.props.height)
    });

    getTdProps = (rowIdx, headerIdx) => {
      const { getCellWidthStyle, props, WIDGETS } = this;
      const cellConfig = props.rows?.[rowIdx]?.fields?.[headerIdx] || null;
      let cellType;

      if (WIDGETS.includes(cellConfig?.type)) {
        cellType = 'WIDGET';
      }

      return { css: getCellWidthStyle(headerIdx, cellType) };
    };

    getTrProps = () => ({
      className: 'gridRow',
      css: this.styles.trWrapper
    });

    renderCell = (cell, colIdx, rowIdx, onChange) => {
      const { components: COMPONENTS, props } = this;
      const { dataTest: dataTestFromProps, tableId } = props;
      const { options, type, value, variant } = cell;
      const Component = ComponentDictionary[type];
      let { dataTest } = cell;

      dataTest =
        dataTest || `cellDataTest-${dataTestFromProps}-${rowIdx}-${colIdx}`;

      const baseProps = {
        cell,
        colIdx,
        dataTest,
        rowIdx,
        tableId,
        value,
        ...(type !== COMPONENTS.STATUS_BADGE ? cell : {})
      };

      switch (type) {
        case COMPONENTS.BUTTON:
        case COMPONENTS.ICON:
          return (
            <Component
              {...baseProps}
              onClick={() => onChange(null, cell, rowIdx, colIdx)}
            />
          );

        case COMPONENTS.CHECKBOX:
          return (
            <Component
              {...baseProps}
              checked={value || false}
              onCheck={(e, val) => onChange(val, cell, rowIdx, colIdx)}
            />
          );

        case COMPONENTS.COMBOBOX:
        case COMPONENTS.DROPDOWN:
        case COMPONENTS.INPUT:
          return <Component {...baseProps} onCellChange={onChange} />;

        case COMPONENTS.ICON_MENU:
          return (
            <Component
              {...baseProps}
              {...options}
              dataTest={dataTest}
              onSelect={(e, val) => onChange(val, cell, rowIdx, colIdx)}
            />
          );

        case COMPONENTS.LINK:
          return <Component {...baseProps} onClick={onChange} />;

        case COMPONENTS.STATIC_FIELD:
          return <Component label={value} variant={variant} />;

        case COMPONENTS.STATUS_BADGE:
          return <Component {...baseProps} />;

        default:
          return <div />;
      }
    };

    getProps = () => {
      const {
        getCommonProps,
        getTbodyProps,
        getTdProps,
        getThProps,
        getTheadProps,
        getTrProps,
        renderCell,
        styles
      } = this;

      const { renderTableFilter, ...props } = getCommonProps();

      return {
        ...props,
        getTbodyProps,
        getTdProps,
        getThProps,
        getTheadProps,
        getTrProps,
        renderCell,
        renderTableFilter: renderTableFilter(renderCell),
        styles
      };
    };

    render() {
      return <BaseGrid {...this.getProps()} />;
    }
  };

export default SettingsDecorator;
